import { PropsWithChildren, useMemo } from 'react'
import { useAuthUserProfile } from '@soniq/util-auth'
import { FeatureFlagsContext } from './FeatureFlagsContext.ts'
import { resolveFeatureFlags } from './types.ts'

/**
 * Reads the authenticated user's Keycloak groups from the JWT - surfaced by
 * oidc-client-ts as `profile.groups` (mapped into the ID token by the
 * `groups` group-membership mapper on the `soniq-dev` client) - and resolves
 * them into feature flags. Re-resolves whenever the user's groups change
 * (sign-in, token refresh, sign-out).
 *
 * Must be mounted inside the auth provider so `useAuthUserProfile` has a
 * value once the user is authenticated.
 */
export function FeatureFlagsProvider({
  children,
}: PropsWithChildren) {
  const profile = useAuthUserProfile()
  const groups =
    (profile?.groups as string[] | undefined) ?? undefined

  // Key on a stable string so we only re-resolve when the groups themselves
  // change, not on every unrelated re-render of the auth context.
  const groupsKey = groups ? groups.join(' ') : ''

  const value = useMemo(
    () => resolveFeatureFlags(groups),
    // groupsKey is the serialised identity of `groups`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groupsKey]
  )

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  )
}
